import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Grid, TextField, Button } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import BlockIcon from "@mui/icons-material/Block";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  StyledWrapper,
  StyledTitle,
  ButtonContainer,
  StyledTextField,
} from "./UserProfile.style";
import BackButton from "components/shared/BackButton";
import DrawerCM from "components/shared/Drawer";
import SalesCard from "components/shared/SalesCard";
import TableCM from "components/shared/Table";
import DatePickerCM from "components/shared/DatePicker";
import PopperCM from "components/shared/Popper";
import FilterBox from "components/shared/FilterBox";
import SelectBox from "components/shared/SelectBox";
import GenarateHeadCell from "helper/generate-head-cell";
import useHttp from "hooks/use-http";
import {
  blockUserRequest,
  getOrdersRequest,
  getProductsRequest,
  getProductStatsRequest,
  getUserInfoRequest,
  unBlockUserRequest,
  updateNormalUserRequest,
  getUserAddressRequest,
} from "api/requests";
import LoadingCM from "components/shared/Loading";
import Toast from "helper/toast";
import { editNormalUserSchema } from "validations";
import PaginationCM from "components/shared/Pagination";
import Modal from "components/shared/Modal/Block";
import { PAGINATION_LIMIT } from "constants";
import spliter from "helper/digit-spliter";
import useFilter from "hooks/use-filter";

const headCells = GenarateHeadCell("userProfile");
const headCellsAddress = GenarateHeadCell("userAddress");
let initialPage = 1;

////////////////////////////
const formList = [
  {
    label: "نام",
    type: "text",
    id: "full_name",
    required: true,
    readOnly: false,
  },
  {
    label: "ایمیل",
    type: "email",
    id: "email",
    readOnly: false,
  },
];

export default function UserProfile() {
  const navigate = useNavigate();
  const params = useParams();
  const filterCTX = useFilter("factor");
  const { request, loading, setLoading } = useHttp();
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(editNormalUserSchema),
  });

  const [user, setuser] = useState();
  const [address, setAddress] = useState({ data: [], count: 0 });
  const [orders, setOrders] = useState({ count: 0, data: [] });
  const [salesCard, setSalesCard] = useState([]);
  const [page, setPage] = useState(initialPage);
  const [products, setProducts] = useState([]);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [datePoper, setDatePoper] = useState({ open: false, anchorEl: null });
  const [breadPoper, setBreadPoper] = useState({ open: false, anchorEl: null });

  const handleBlockModal = () => setShowBlockModal((state) => !state);

  //////////////// FORM ////////////////
  const onSubmit = async (values) => {
    delete values.message;
    values.id = params.userId;

    const res = await request({
      url: updateNormalUserRequest,
      values,
    });

    if (res) {
      Toast({
        type: "success",
        title: "ویرایش کاربر",
      });
    }
  };

  const onBlockUser = async (values) => {
    if (getValues("is_blocked")) {
      const res = await request({
        url: unBlockUserRequest,
        values: params.userId,
      });

      if (res) {
        Toast({
          type: "success",
          title: "رفع مسدودیت کاربر",
        });
      }
    } else {
      handleBlockModal();

      if (values.message.trim() === "")
        return Toast({ type: "empty", title: " دلیل مسدود سازی " });

      const res = await request({
        url: blockUserRequest,
        values: { id: params.userId, message: values.message },
      });

      if (res) {
        Toast({
          type: "success",
          title: "مسدودیت کاربر",
        });
      }
    }
    getUserInfo();
  };

  const handelSearch = (e) => getOrders(e);

  const getOrders = async (search) => {
    setLoading(true);
    await getUserInfo();
    const orderRes = await request({
      url: getOrdersRequest,
      values: {
        skip: search ? 0 : (page - 1) * PAGINATION_LIMIT,
        limit: search ? 100 : PAGINATION_LIMIT,
        search,
        userId: params.userId,
      },
      isMulty: true,
    });

    setLoading(false);

    if (orderRes) {
      if (orderRes.count < 1) {
        return setOrders({
          count: orderRes.count,
          data: [{ content: "هیچ سفارشی یافت نشد" }],
        });
      }
      const transormedOrderData = orderRes.data.map((items) => ({
        id: items.id,
        orderId: items.order_number,
        payment_status: items.payment_status == "failed" ? "ناموفق" : "موفق",
        date: items.created_at?.jdate.split(" ")[0],
        totalPrice: spliter(items.total_price),
      }));

      setOrders({ count: orderRes.count, data: transormedOrderData });
    }
  };

  const getAddress = useCallback(async () => {
    setLoading(true);
    await getUserInfo();
    const res = await request({
      url: getUserAddressRequest,
      values: {
        userId: params.userId,
      },
    });

    // setLoading(false);
    if (res) {
      if (res.count < 1) {
        return setAddress({
          count: res.count,
          data: [{ content: "هیچ آدرسی یافت نشد" }],
        });
      }
      let transformedData = res.data.map((items) => ({
        id: items.id,
        address: items?.address,
        state: items?.state,
        city: items?.city,
        postal_code: items?.postal_code,
      }));

      setAddress({ data: transformedData, count: res.count });
    }
  }, [page, request]);

  const getUserInfo = useCallback(async () => {
    let res = await request({
      url: getUserInfoRequest,
      values: params.userId,
      isMulty: true,
    });
    // setLoading(false);

    if (res) {
      reset({
        full_name: res.full_name,
        phone_number: res.phone_number,
        email: res.email,
        is_blocked: res.is_blocked,
      });
      setuser(res);
    }
  }, [request, params.userId, reset]);

  useEffect(() => {
    getUserInfo();
    getOrders();
    getAddress();
  }, [getUserInfo]);

  return (
    <DrawerCM title="مشخصات کاربر" onSearch={handelSearch}>
      {loading ? (
        <LoadingCM />
      ) : (
        <Grid container spacing={1}>
          <Grid item mt={3}>
            <BackButton />
          </Grid>

          <Grid item mb={3} xs={12}>
            <StyledWrapper>
              <StyledTitle>مشخصات کاربر {user?.phone_number}</StyledTitle>
              <Grid container>
                {formList.map((items, index) => (
                  <Grid
                    item
                    md={formList.length - 3 === index ? 12 : 6}
                    xs={12}
                    p={1.5}
                    key={"profile" + index}
                  >
                    <TextField
                      id={items.id}
                      label={items.label}
                      variant="outlined"
                      required={items.required}
                      fullWidth
                      {...register(items.id)}
                      disabled={items.disabled}
                      error={Boolean(errors[items.id])}
                      helperText={errors[items.id]?.message}
                      InputProps={{
                        readOnly: items.readOnly,
                      }}
                    />
                  </Grid>
                ))}

                <ButtonContainer>
                  <Button
                    color="warning"
                    startIcon={<BlockIcon />}
                    variant="contained"
                    sx={{ mt: 1, mr: 1 }}
                    onClick={
                      getValues("is_blocked") ? onBlockUser : handleBlockModal
                    }
                  >
                    {getValues("is_blocked") ? "رفع مسدودیت" : "مسدود کردن"}
                  </Button>
                  <Button
                    color="success"
                    startIcon={<CheckIcon />}
                    variant="contained"
                    sx={{ mt: 1, mr: 1 }}
                    onClick={handleSubmit(onSubmit)}
                  >
                    ویرایش
                  </Button>
                </ButtonContainer>
              </Grid>
            </StyledWrapper>
          </Grid>

          <Modal
            show={showBlockModal}
            onClose={handleBlockModal}
            title="مسدود کردن کاربر"
          >
            <StyledTextField
              id="message"
              label="دلیل مسدود سازی"
              variant="outlined"
              fullWidth
              multiline
              required
              rows={4}
              {...register("message")}
            />
            <ButtonContainer Tcenter>
              <Button
                startIcon={<BlockIcon />}
                variant="contained"
                sx={{ mt: 1 }}
                onClick={handleSubmit(onBlockUser)}
                color="warning"
              >
                مسدود کردن
              </Button>
            </ButtonContainer>
          </Modal>

          <Grid item xs={12} my={2}>
            <TableCM
              title="سفارشات کاربر"
              rows={orders.data}
              headCells={headCells}
              click={(orderId) => navigate("/orders/" + orderId)}
              page={page}
            />
          </Grid>
          <PaginationCM
            handleChange={(e, val) => {
              initialPage = val;
              setPage(val);
            }}
            page={page}
            count={orders.count}
          />

          <Grid item xs={12} my={2}>
            <TableCM
              title="آدرس های کاربر"
              rows={address.data}
              headCells={headCellsAddress}
              click={(addressId) => navigate("/address/" + addressId)}
              page={page}
              // addAddress
            />
          </Grid>
        </Grid>
      )}
    </DrawerCM>
  );
}
